import React, { useEffect, useState, useRef } from "react";
import {
    getThreadById,
    replyThread,
    closeThread,
    reopenThread,
} from "../../../api/serviceRecordApi";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { REACT_APP_API_URL } from "../../../env";

const ServiceRecordDetail = () => {
    const { id } = useParams();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.user?.LevelId === 1;
    const currentUserId = user?.employee?.id || user?.user?.customerId;
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [attachment, setAttachment] = useState(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        fetchThread();
    }, []);

    const fetchThread = async () => {
        const res = await getThreadById(id);
        setThread(res.data.thread);
        setMessages(res.data.messages);
    };

    const handleReply = async () => {
        if (!reply) return;

        const formData = new FormData();
        formData.append("threadId", id);
        formData.append("message", reply);

        if (attachment) formData.append("attachment", attachment);

        await replyThread(formData);

        setReply("");
        setAttachment(null);
        fetchThread();
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";

        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC"
        });
    };

    const handleClose = async () => {
        await closeThread(id);
        fetchThread();
    };

    const handleReopen = async () => {
        await reopenThread(id);
        fetchThread();
    };

    if (!thread) return null;

    return (
        <div className="container mt-4">

            {/* Thread Header */}
            <div className="card shadow-sm mb-3">
                <div className="card-body d-flex justify-content-between align-items-center">

                    <div>
                        <h5 className="fw-semibold mb-1">
                            #{thread.ThreadID} — {thread.Subject}
                        </h5>

                        <div className="small text-muted">
                            <span className="me-3">
                                <strong>Employee:</strong> {thread.EmployeeName}
                            </span>

                            <span className="me-3">
                                <strong>Authorized By:</strong> {thread.AuthorizedBy}
                            </span>

                            <span>
                                <strong>Created:</strong> {formatDateTime(thread.CreatedDate)}
                            </span>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">

                        {/* Status Badge */}
                        {thread.Status === "Open" ? (
                            <span className="badge bg-success px-3 py-2">Open</span>
                        ) : (
                            <span className="badge bg-secondary px-3 py-2">Closed</span>
                        )}

                        {/* Admin Action Buttons */}
                        {isAdmin && thread.Status === "Open" && (
                            <button
                                className="btn btn-sm btn-danger"
                                onClick={handleClose}
                            >
                                Close Thread
                            </button>
                        )}

                        {isAdmin && thread.Status === "Closed" && (
                            <button
                                className="btn btn-sm btn-warning"
                                onClick={handleReopen}
                            >
                                Reopen
                            </button>
                        )}

                    </div>

                </div>
            </div>

            {/* Message Thread */}
            <div className="card shadow-sm">
                <div
                    className="card-body"
                    style={{ maxHeight: "450px", overflowY: "auto" }}
                >
                    {messages.map((msg) => {

                        const isMine = Number(msg.SenderID) === Number(currentUserId);

                        if (msg.IsSystemMessage) {
                            return (
                                <div key={msg.MessageID} className="text-center my-3">
                                    <span className="badge bg-light text-dark">
                                        {msg.MessageText}
                                    </span>
                                </div>
                            );
                        }

                        return (
                            <div
                                key={msg.MessageID}
                                className={`d-flex mb-2 ${isMine ? "justify-content-end" : "justify-content-start"}`}
                            >
                                <div
                                    className={`rounded px-3 py-2 shadow-sm ${Number(msg.SenderID) === Number(currentUserId)
                                        ? "bg-primary text-white"
                                        : "bg-light"
                                        }`}
                                    style={{ maxWidth: "70%" }}
                                >

                                    {/* Sender */}
                                    <div className="fw-semibold small">
                                        {msg.SenderName === "Administrator" ? "Admin" : msg.SenderName}
                                    </div>

                                    {/* Message */}
                                    <div className="small">
                                        {msg.MessageText}
                                    </div>

                                    {/* Attachment */}
                                    {msg.FilePath && (
                                        <div className="small mt-1">
                                            <a
                                                href={`${REACT_APP_API_URL}${msg.FilePath}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-decoration-none"
                                            >
                                                📎 Attachment
                                            </a>
                                        </div>
                                    )}

                                    {/* Time */}
                                    <div className="text-muted small mt-1">
                                        {formatDateTime(msg.CreatedDate)}
                                    </div>

                                </div>
                                <div ref={messagesEndRef} />
                            </div>
                        );
                    })}
                </div>

                {/* Reply Section */}
                {thread.Status === "Open" && (
                    <div className="card-footer bg-light">

                        <textarea
                            className="form-control mb-2"
                            rows="3"
                            placeholder="Write your reply..."
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                        />

                        <div className="d-flex justify-content-between align-items-center">

                            <input
                                type="file"
                                className="form-control form-control-sm w-50"
                                onChange={(e) => setAttachment(e.target.files[0])}
                            />

                            <button
                                className="btn btn-primary ms-2"
                                onClick={handleReply}
                                disabled={!reply.trim()}
                            >
                                Send Reply
                            </button>

                        </div>

                    </div>
                )}

            </div>
        </div>
    );
};

export default ServiceRecordDetail;